import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { AuthContext } from "../context/Auth";
import { firebaseApp } from "@healthdesk/shared-services";
import { tierAtLeast, isDevAuthFirestoreUser } from "@healthdesk/shared-utils";

/**
 * Firestore Timestamp, ISO string, epoch, or Date → Date (or null).
 * @param {unknown} timestamp
 * @returns {Date | null}
 */
const toDate = (timestamp) => {
  if (timestamp == null) return null;
  if (typeof timestamp === "object" && "toDate" in timestamp) {
    const maybe = /** @type {{ toDate?: () => Date }} */ (timestamp).toDate;
    if (typeof maybe === "function") return maybe.call(timestamp);
  }
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return null;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dbError, setDbError] = useState(null);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  const {
    user,
    subscription, // This is actually the user ROLE (patient, facility, etc.) - confusing naming!
    subscriptionData, // This is the actual Stripe subscription object with status
    userData,
    userLoading,
    logout,
    listings,
    invoices,
    // Medication management
    medications,
    reminderStatuses,
    medicationsLoading,
    addMedication,
    updateMedication,
    deleteMedication,
    refreshReminderStatuses,
    loadMedications,

    // Tracking data management
    trackingData,
    loadTrackingData,
    clearTrackingData,
    forceResetTrackingData,
  } = context;

  const isAuthenticated = useMemo(() => {
    return user !== null && !userLoading;
  }, [user, userLoading]);

  const isVerified = useMemo(() => {
    return isAuthenticated && user?.emailVerified;
  }, [isAuthenticated, user]);

  const hasRole = useMemo(() => {
    return isVerified && userData?.role;
  }, [isVerified, userData]);

  const hasValidSubscription = useMemo(() => {
    // Simple trial check - just date comparison
    const hasActiveTrial =
      userData?.trialExpiresAt && toDate(userData.trialExpiresAt) > new Date();

    // Check if subscription exists and has active status
    const hasActiveSubscription =
      subscriptionData && subscriptionData.status === "active";

    // Check if daily pass is active
    const dailyPassEnd = toDate(userData?.dailyPassExpiresAt);
    const hasActiveDailyPass =
      dailyPassEnd != null && dailyPassEnd > new Date();

    return (
      hasRole && (hasActiveTrial || hasActiveSubscription || hasActiveDailyPass)
    );
  }, [hasRole, subscriptionData, userData]);

  const hasActiveTrial = useMemo(() => {
    if (!userData?.trialExpiresAt) return false;
    return toDate(userData.trialExpiresAt) > new Date();
  }, [userData?.trialExpiresAt]);

  // Check if subscription exists and is active
  const hasActiveSubscription = useMemo(() => {
    return subscriptionData && subscriptionData.status === "active";
  }, [subscriptionData]);

  const hasActiveDailyPass = useMemo(() => {
    const end = toDate(userData?.dailyPassExpiresAt);
    return end != null && end > new Date();
  }, [userData?.dailyPassExpiresAt]);

  // deprecated: use hasTierAccess('medium') instead
  const isPlus = hasActiveSubscription;

  const subscriptionTier = useMemo(() => {
    // Phone dev auth: isDevAccount and/or @dev.local email (legacy user docs may lack the flag).
    // Optional explicit subscriptionTier on the dev user doc wins for tier UX tests (free / small / medium / large).
    const isDevByAuthEmail =
      typeof user?.email === "string" &&
      user.email.toLowerCase().endsWith("@dev.local");
    if (isDevAuthFirestoreUser(userData) || isDevByAuthEmail) {
      const t = userData?.subscriptionTier;
      if (t === 'free' || t === 'small' || t === 'medium' || t === 'large') return t;
      return 'large';
    }
    if (hasActiveSubscription) {
      return userData?.subscriptionTier || 'medium'; // migration fallback
    }
    if (hasActiveTrial || hasActiveDailyPass) return 'medium';
    return 'free';
  }, [
    user?.email,
    userData,
    userData?.isDevAccount,
    userData?.email,
    userData?.subscriptionTier,
    hasActiveSubscription,
    hasActiveTrial,
    hasActiveDailyPass,
  ]);

  const isTierFree = subscriptionTier === 'free';
  const isTierSmall = subscriptionTier === 'small';
  const isTierMedium = subscriptionTier === 'medium';
  const isTierLarge = subscriptionTier === 'large';

  const hasTierAccess = useCallback(
    (minTier) => tierAtLeast(subscriptionTier, minTier),
    [subscriptionTier],
  );

  // Check if subscription is cancelled or past due
  const hasLapsedSubscription = useMemo(() => {
    return (
      subscriptionData &&
      ["canceled", "past_due", "unpaid"].includes(subscriptionData.status)
    );
  }, [subscriptionData]);

  const canStartTrial = useMemo(() => {
    return (
      hasRole &&
      ["patient", "professional", "facility"].includes(userData?.role) &&
      !userData?.hasUsedTrial &&
      !hasActiveSubscription
    );
  }, [hasRole, userData?.role, userData?.hasUsedTrial, hasActiveSubscription]);

  const trialExpired = useMemo(() => {
    // Only true if trial was used, no active trial, NO subscription, and trial wasn't consumed by upgrade
    return (
      userData?.hasUsedTrial &&
      !hasActiveTrial &&
      !subscriptionData &&
      !userData?.trialConsumedByUpgrade
    );
  }, [
    userData?.hasUsedTrial,
    hasActiveTrial,
    subscriptionData,
    userData?.trialConsumedByUpgrade,
  ]);

  const activeSubscriptionRole = useMemo(() => {
    if (!isVerified) return undefined;

    // Check for active trial first
    if (hasActiveTrial) {
      return userData?.role;
    }

    // Check for daily pass
    if (hasActiveDailyPass) {
      return userData?.role;
    }

    // Then check for active stripe subscription
    if (hasActiveSubscription) {
      return userData?.role;
    }

    // No valid subscription found
    return undefined;
  }, [
    isVerified,
    hasActiveTrial,
    hasActiveDailyPass,
    hasActiveSubscription,
    userData?.role,
  ]);

  const canUseFreeTrial = useMemo(() => {
    return subscription === "facility" && !userData?.hasUsedFreeTrial;
  }, [subscription, userData]);

  const formattedZipSubscriptions = useMemo(() => {
    if (!listings) return {};

    const formatted = {};
    Object.entries(listings).forEach(([zipCode, listing]) => {
      if (listing.type === "featured") {
        formatted[zipCode] = {
          ...listing,
          // Ensure amount_paid is properly formatted as a number
          amount_paid: listing.amount_paid
            ? typeof listing.amount_paid === "string"
              ? parseInt(listing.amount_paid, 10)
              : listing.amount_paid
            : 0,
          // Ensure status is a string
          status: listing.status || "pending",
        };
      }
    });
    return formatted;
  }, [listings]);

  const formattedZipPromotions = useMemo(() => {
    if (!listings) return {};

    const formatted = {};
    Object.entries(listings).forEach(([zipCode, listing]) => {
      if (listing.type === "promotion") {
        formatted[zipCode] = {
          ...listing,
          status: listing.status || "pending",
          active: listing.active || false,
        };
      }
    });
    return formatted;
  }, [listings]);

  const resendEmail = async () => {
    setLoading(true);
    setDbError(null);

    try {
      await user.sendEmailVerification();
      setSubmitted(true);
    } catch (error) {
      setDbError(error.message);
      console.error("Email verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setDbError(null);
      setSubmitted(false);
    }
  }, [user]);

  const pendingInvoice = useMemo(() => {
    return invoices?.find((inv) => inv.status === "open");
  }, [invoices]);

  return {
    // User state
    user,
    userData,
    subscription,
    subscriptionData,
    subscriptionTier,
    userLoading,
    zipSubscriptions: formattedZipSubscriptions,
    zipPromotions: formattedZipPromotions,

    // Verification state
    loading,
    submitted,
    dbError,

    // Computed state
    isAuthenticated,
    isVerified,
    hasRole,
    hasValidSubscription,
    hasActiveDailyPass,
    hasActiveTrial,
    hasActiveSubscription,
    isPlus,
    isTierFree,
    isTierSmall,
    isTierMedium,
    isTierLarge,
    hasTierAccess,
    hasLapsedSubscription,
    canStartTrial,
    trialExpired,
    activeSubscriptionRole,
    canUseFreeTrial,

    detectedCountry: userData?.detectedCountry || null,

    // Methods
    resendEmail,
    logout,

    // Additional state
    invoices,
    pendingInvoice,

    // Medication management
    medications,
    reminderStatuses,
    medicationsLoading,
    addMedication,
    updateMedication,
    deleteMedication,
    refreshReminderStatuses,
    loadMedications,

    // Tracking data management
    trackingData,
    loadTrackingData,
    clearTrackingData,
    forceResetTrackingData,
  };
};
